/**
 * Stakeholders Panel Component - Sprint 18
 * Displays and manages stakeholders for projects/programs
 * 
 * Features:
 * - List stakeholders with RACI roles
 * - CRUD operations (add/edit/remove)
 * - Influence/Interest matrix view
 * - Demo data seeding
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, Plus, Edit2, Trash2, Loader2, RefreshCw, X, Mail, Phone,
  Building, User, Shield, Star, ChevronDown, ChevronRight, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('simpliview_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Role badge colors
const ROLE_COLORS = {
  'Sponsor': 'bg-purple-100 text-purple-700',
  'Owner': 'bg-blue-100 text-blue-700',
  'Lead': 'bg-green-100 text-green-700',
  'Member': 'bg-gray-100 text-gray-700',
  'Advisor': 'bg-amber-100 text-amber-700',
  'Consumer': 'bg-cyan-100 text-cyan-700'
};

// RACI badge colors
const RACI_COLORS = {
  'R': 'bg-red-100 text-red-700',
  'A': 'bg-orange-100 text-orange-700',
  'C': 'bg-blue-100 text-blue-700',
  'I': 'bg-gray-100 text-gray-600'
};

const RACI_LABELS = {
  'R': 'Responsible',
  'A': 'Accountable',
  'C': 'Consulted',
  'I': 'Informed'
};

// Stakeholder Card
const StakeholderCard = ({ stakeholder, onEdit, onDelete }) => (
  <div className="bg-white rounded-lg border p-4 hover:shadow-md transition-shadow" data-testid={`stakeholder-${stakeholder.stakeholder_id}`}>
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-medium">
          {stakeholder.name?.charAt(0)?.toUpperCase() || 'S'}
        </div>
        <div>
          <h3 className="font-medium text-gray-900">{stakeholder.name}</h3>
          {stakeholder.email && (
            <p className="text-sm text-gray-500 flex items-center gap-1">
              <Mail className="w-3 h-3" />
              {stakeholder.email}
            </p>
          )}
        </div>
      </div>
      
      <div className="flex items-center gap-1">
        <button
          onClick={() => onEdit(stakeholder)}
          className="p-1.5 hover:bg-gray-100 rounded text-gray-500 hover:text-gray-700"
          title="Edit"
        >
          <Edit2 className="w-4 h-4" />
        </button>
        <button
          onClick={() => onDelete(stakeholder.stakeholder_id)}
          className="p-1.5 hover:bg-red-100 rounded text-gray-500 hover:text-red-600"
          title="Delete"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
    
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${ROLE_COLORS[stakeholder.role] || 'bg-gray-100'}`}>
        {stakeholder.role}
      </span>
      
      {stakeholder.raci && (
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${RACI_COLORS[stakeholder.raci] || 'bg-gray-100'}`} title={RACI_LABELS[stakeholder.raci]}>
          RACI: {stakeholder.raci}
        </span>
      )}
      
      {stakeholder.department && (
        <span className="flex items-center gap-1 text-xs text-gray-500">
          <Building className="w-3 h-3" />
          {stakeholder.department}
        </span>
      )}
    </div>
    
    {/* Influence/Interest Indicators */}
    <div className="mt-3 pt-3 border-t flex items-center gap-4 text-xs text-gray-500">
      <div className="flex items-center gap-1">
        <Star className={`w-3 h-3 ${stakeholder.influence === 'high' ? 'text-yellow-500 fill-yellow-500' : ''}`} />
        Influence: <span className="capitalize">{stakeholder.influence}</span>
      </div>
      <div className="flex items-center gap-1">
        <Shield className={`w-3 h-3 ${stakeholder.interest === 'high' ? 'text-blue-500' : ''}`} />
        Interest: <span className="capitalize">{stakeholder.interest}</span>
      </div>
    </div>
  </div>
);

// Add/Edit Modal
const StakeholderModal = ({ isOpen, onClose, onSubmit, stakeholder, entityType, entityId }) => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    role: 'Member',
    department: '',
    organization: '',
    phone: '',
    raci: '',
    influence: 'medium',
    interest: 'medium',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  
  useEffect(() => {
    if (stakeholder) {
      setForm({
        name: stakeholder.name || '',
        email: stakeholder.email || '',
        role: stakeholder.role || 'Member',
        department: stakeholder.department || '',
        organization: stakeholder.organization || '',
        phone: stakeholder.phone || '',
        raci: stakeholder.raci || '',
        influence: stakeholder.influence || 'medium',
        interest: stakeholder.interest || 'medium',
        notes: stakeholder.notes || ''
      });
    } else {
      setForm({
        name: '',
        email: '',
        role: 'Member',
        department: '',
        organization: '',
        phone: '',
        raci: '',
        influence: 'medium',
        interest: 'medium',
        notes: ''
      });
    }
  }, [stakeholder]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    
    setSubmitting(true);
    try {
      await onSubmit({
        ...form,
        entity_type: entityType,
        entity_id: entityId
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">
            {stakeholder ? 'Edit Stakeholder' : 'Add Stakeholder'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full border rounded px-3 py-2 mt-1"
              required
            />
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700">Role</label>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full border rounded px-3 py-2 mt-1"
              >
                <option value="Sponsor">Sponsor</option>
                <option value="Owner">Owner</option>
                <option value="Lead">Lead</option>
                <option value="Member">Member</option>
                <option value="Advisor">Advisor</option>
                <option value="Consumer">Consumer</option>
              </select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">RACI</label>
              <select
                value={form.raci}
                onChange={(e) => setForm({ ...form, raci: e.target.value })}
                className="w-full border rounded px-3 py-2 mt-1"
              >
                <option value="">None</option>
                <option value="R">R - Responsible</option>
                <option value="A">A - Accountable</option>
                <option value="C">C - Consulted</option>
                <option value="I">I - Informed</option>
              </select>
            </div>
          </div>
          
          <div>
            <label className="text-sm font-medium text-gray-700">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full border rounded px-3 py-2 mt-1"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700">Department</label>
              <input
                type="text"
                value={form.department}
                onChange={(e) => setForm({ ...form, department: e.target.value })}
                className="w-full border rounded px-3 py-2 mt-1"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Phone</label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full border rounded px-3 py-2 mt-1"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700">Influence</label>
              <select
                value={form.influence}
                onChange={(e) => setForm({ ...form, influence: e.target.value })}
                className="w-full border rounded px-3 py-2 mt-1"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Interest</label>
              <select
                value={form.interest}
                onChange={(e) => setForm({ ...form, interest: e.target.value })}
                className="w-full border rounded px-3 py-2 mt-1"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
          </div>
          
          <div>
            <label className="text-sm font-medium text-gray-700">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              className="w-full border rounded px-3 py-2 mt-1"
            />
          </div>
          
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 border rounded-lg font-medium hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {stakeholder ? 'Update' : 'Add'} Stakeholder
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Main Component
const StakeholdersPanel = ({ entityType = 'project', entityId, readOnly = false }) => {
  const [stakeholders, setStakeholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingStakeholder, setEditingStakeholder] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // list, raci
  
  // Load stakeholders
  const loadStakeholders = useCallback(async () => {
    if (!entityId) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/stakeholders/entity/${entityType}/${entityId}`,
        { headers: getAuthHeaders() }
      );
      
      if (res.ok) {
        const data = await res.json();
        setStakeholders(data.stakeholders || []);
      }
    } catch (err) {
      console.error('Failed to load stakeholders:', err);
      toast.error('Failed to load stakeholders');
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);
  
  useEffect(() => {
    loadStakeholders();
  }, [loadStakeholders]);
  
  // Create/Update stakeholder
  const handleSaveStakeholder = async (data) => {
    const url = editingStakeholder
      ? `${API_BASE}/stakeholders/${editingStakeholder.stakeholder_id}`
      : `${API_BASE}/stakeholders`;
    
    const method = editingStakeholder ? 'PATCH' : 'POST';
    
    const res = await fetch(url, {
      method,
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    
    if (res.ok) {
      toast.success(editingStakeholder ? 'Stakeholder updated' : 'Stakeholder added');
      setEditingStakeholder(null);
      loadStakeholders();
    } else {
      const error = await res.json();
      toast.error(error.detail || 'Failed to save stakeholder');
      throw new Error('Failed to save');
    }
  };
  
  // Delete stakeholder
  const handleDelete = async (stakeholderId) => {
    if (!window.confirm('Remove this stakeholder?')) return;
    
    try {
      const res = await fetch(`${API_BASE}/stakeholders/${stakeholderId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        toast.success('Stakeholder removed');
        loadStakeholders();
      } else {
        toast.error('Failed to remove stakeholder');
      }
    } catch (err) {
      toast.error('Failed to remove stakeholder');
    }
  };
  
  // Edit stakeholder
  const handleEdit = (stakeholder) => {
    setEditingStakeholder(stakeholder);
    setShowModal(true);
  };
  
  // Seed demo data
  const handleSeedDemo = async () => {
    setSeeding(true);
    try {
      const res = await fetch(
        `${API_BASE}/stakeholders/demo/seed?entity_type=${entityType}&entity_id=${entityId}`,
        { method: 'POST', headers: getAuthHeaders() }
      );
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Seeded ${data.stakeholders_created} stakeholders`);
        loadStakeholders();
      } else {
        toast.error('Failed to seed demo data');
      }
    } catch (err) {
      toast.error('Failed to seed demo data');
    } finally {
      setSeeding(false);
    }
  };
  
  if (!entityId) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p>Select a {entityType} to view stakeholders</p>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }
  
  return (
    <div className="space-y-4" data-testid="stakeholders-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-800 flex items-center gap-2">
          <Users className="w-5 h-5" />
          Stakeholders ({stakeholders.length})
        </h3>
        
        <div className="flex items-center gap-2">
          {stakeholders.length === 0 && (
            <button
              onClick={handleSeedDemo}
              disabled={seeding || readOnly}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-purple-600 border border-purple-200 rounded-lg hover:bg-purple-50 disabled:opacity-50"
              data-testid="seed-stakeholders"
            >
              {seeding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              Seed Demo
            </button>
          )}
          
          {!readOnly && (
            <button
              onClick={() => { setEditingStakeholder(null); setShowModal(true); }}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              data-testid="add-stakeholder"
            >
              <Plus className="w-4 h-4" />
              Add
            </button>
          )}
          
          <button
            onClick={loadStakeholders}
            className="p-1.5 hover:bg-gray-100 rounded"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {/* Empty State */}
      {stakeholders.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed">
          <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-600 font-medium">No stakeholders yet</p>
          <p className="text-sm text-gray-500 mt-1">Add stakeholders to track project roles and responsibilities</p>
          <button
            onClick={handleSeedDemo}
            disabled={seeding || readOnly}
            className="mt-4 flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg hover:from-purple-600 hover:to-blue-600 mx-auto"
          >
            <Sparkles className="w-4 h-4" />
            Load Demo Stakeholders
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {stakeholders.map(stakeholder => (
            <StakeholderCard
              key={stakeholder.stakeholder_id}
              stakeholder={stakeholder}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
      
      {/* Modal */}
      <StakeholderModal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditingStakeholder(null); }}
        onSubmit={handleSaveStakeholder}
        stakeholder={editingStakeholder}
        entityType={entityType}
        entityId={entityId}
      />
    </div>
  );
};

export default StakeholdersPanel;
